import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertTriangle, CheckCircle2, Save } from "lucide-react";
import { toast } from "sonner";
import { formatMoney, docCurrency } from "@/lib/currency";

// Admin-only: issue many draft Tax Invoices at once, each with the Tally
// voucher number typed in. Every invoice is issued server-side through the
// same path as the single Issue button (POST /tax-invoices/bulk-issue).
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const isPendingCode = (code) => (code || "").startsWith("TALLY-PENDING/");
const tallyNoOf = (inv) => (inv.tally_number_pending || isPendingCode(inv.code) ? "" : inv.code || "");

export default function TaxInvoiceBulkIssueDialog({ open, onOpenChange, invoices, dcMap, onChanged }) {
  const [tab, setTab] = useState("draft");
  const [checked, setChecked] = useState({});
  const [numbers, setNumbers] = useState({});
  const [busy, setBusy] = useState(false);
  const [confirmMissing, setConfirmMissing] = useState(null);
  const [result, setResult] = useState(null);
  const [issuedSearch, setIssuedSearch] = useState("");
  const [progress, setProgress] = useState(null);

  const drafts = useMemo(() => invoices.filter((i) => i.status === "draft"), [invoices]);
  const issued = useMemo(() => {
    const q = issuedSearch.trim().toLowerCase();
    return invoices
      .filter((i) => i.status === "issued")
      .filter((i) => !q || [i.code, i.dc_code, i.customer_name].some((v) => (v || "").toLowerCase().includes(q)))
      .sort((a, b) => new Date(b.issued_at || 0) - new Date(a.issued_at || 0));
  }, [invoices, issuedSearch]);

  useEffect(() => {
    if (!open) return;
    setTab("draft"); setChecked({}); setResult(null); setConfirmMissing(null); setIssuedSearch("");
    setNumbers(Object.fromEntries(invoices.map((i) => [i.id, tallyNoOf(i)])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fill in numbers for invoices that appear after a reload, keep what was typed.
  useEffect(() => {
    setNumbers((n) => {
      const next = { ...n };
      invoices.forEach((i) => { if (next[i.id] === undefined) next[i.id] = tallyNoOf(i); });
      return next;
    });
  }, [invoices]);

  const selectedIds = drafts.filter((d) => checked[d.id]).map((d) => d.id);
  const allChecked = drafts.length > 0 && selectedIds.length === drafts.length;
  const toggleAll = (v) => setChecked(v ? Object.fromEntries(drafts.map((d) => [d.id, true])) : {});
  const numberFor = (id) => (numbers[id] || "").trim();

  // Sent in small batches so a large selection (hundreds of invoices, each
  // pushed to Tally) never runs into one long request timing out.
  const BATCH = 20;
  const runIssue = async (ids, withoutTallyNumber = false) => {
    setBusy(true);
    setConfirmMissing(null);
    const total = { issued: [], skipped: [], failed: [] };
    try {
      for (let i = 0; i < ids.length; i += BATCH) {
        setProgress({ done: i, total: ids.length });
        const chunk = ids.slice(i, i + BATCH);
        try {
          const { data } = await api.post("/tax-invoices/bulk-issue", {
            invoices: chunk.map((id) => ({ invoice_id: id, tally_number: numberFor(id) })),
            issue_without_tally_number: withoutTallyNumber,
          });
          total.issued.push(...data.issued);
          total.skipped.push(...data.skipped);
          total.failed.push(...data.failed);
        } catch (e) {
          const err = formatApiError(e.response?.data?.detail) || "Bulk issue failed";
          chunk.forEach((id) => total.failed.push({ invoice_id: id, dc_code: drafts.find((d) => d.id === id)?.dc_code, error: err }));
        }
      }
      setResult(total);
      setChecked({});
      // Straight to where the Tally numbers can be filled in afterwards.
      if (withoutTallyNumber && total.issued.length) { setTab("issued"); setIssuedSearch(""); }
      if (total.issued.length) toast.success(`${total.issued.length} invoice(s) issued`);
      if (total.failed.length) toast.error(`${total.failed.length} invoice(s) could not be issued`);
      await onChanged?.();
    } finally { setBusy(false); setProgress(null); }
  };

  const issueSelected = () => {
    if (!selectedIds.length) return toast.error("Select at least one invoice");
    const missing = drafts.filter((d) => checked[d.id] && !numberFor(d.id));
    if (missing.length) { setConfirmMissing(missing); return; }
    runIssue(selectedIds);
  };

  const saveNumber = async (inv) => {
    const n = numberFor(inv.id);
    if (!n) return toast.error("Enter the Tally Invoice No.");
    setBusy(true);
    try {
      const { data } = await api.post(`/tax-invoices/${inv.id}/tally-invoice-number`, { tally_number: n });
      toast.success(`Invoice No. saved — ${data.code}`);
      await onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save the Tally Invoice No.");
    } finally { setBusy(false); }
  };

  const tabBtn = (key, label) => (
    <button type="button" onClick={() => setTab(key)} data-testid={`bulk-issue-tab-${key}`}
      className={`px-3 py-1.5 text-sm rounded-md border ${tab === key ? "bg-blue-600 text-white border-blue-600" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
      {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="bulk-issue-dialog">
        <DialogHeader><DialogTitle>Bulk Issue Tax Invoices</DialogTitle></DialogHeader>
        <div className="flex gap-2">
          {tabBtn("draft", `Draft (${drafts.length})`)}
          {tabBtn("issued", "Issued — edit Tally No.")}
        </div>

        {result && (
          <div className="text-sm border border-slate-200 rounded-md p-3 space-y-1" data-testid="bulk-issue-result">
            <div className="flex items-center gap-1.5 text-emerald-700">
              <CheckCircle2 className="w-4 h-4" /> Issued {result.issued.length}
              {result.skipped.length > 0 && <span className="text-amber-700"> · Skipped {result.skipped.length} (no Tally No.)</span>}
              {result.failed.length > 0 && <span className="text-red-700"> · Failed {result.failed.length}</span>}
            </div>
            {result.failed.map((f) => (
              <div key={f.invoice_id} className="text-xs text-red-700">
                <span className="mono">{f.dc_code || f.code || f.invoice_id}</span>: {f.error}
              </div>
            ))}
          </div>
        )}

        {tab === "draft" && (
          <div className="overflow-x-auto border border-slate-200 rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 w-8">
                    <Checkbox checked={allChecked} onCheckedChange={(v) => toggleAll(!!v)} disabled={!drafts.length}
                      aria-label="Select all" data-testid="bulk-issue-select-all" />
                  </th>
                  <th className="px-3 py-2 text-left">Invoice Ref</th>
                  <th className="px-3 py-2 text-left">DC</th>
                  <th className="px-3 py-2 text-left">Customer</th>
                  <th className="px-3 py-2 text-left">DC Dispatch</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-left">Tally Invoice No.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {drafts.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">No invoices in Draft.</td></tr>
                )}
                {drafts.map((inv) => {
                  const dc = dcMap[inv.dc_id];
                  const missing = checked[inv.id] && !numberFor(inv.id);
                  return (
                    <tr key={inv.id} className={checked[inv.id] ? "bg-blue-50/40" : ""} data-testid={`bulk-issue-row-${inv.id}`}>
                      <td className="px-3 py-2">
                        <Checkbox checked={!!checked[inv.id]} onCheckedChange={(v) => setChecked((c) => ({ ...c, [inv.id]: !!v }))}
                          aria-label={`Select ${inv.code}`} />
                      </td>
                      <td className="px-3 py-2 mono text-xs">{inv.code}</td>
                      <td className="px-3 py-2 mono text-xs">{inv.dc_code || "—"}</td>
                      <td className="px-3 py-2">{inv.customer_name || "—"}</td>
                      <td className="px-3 py-2 mono text-xs">{fmtDate(dc?.dispatch_date || dc?.dispatched_at)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatMoney(Number(inv.grand_total || 0), docCurrency(inv))}</td>
                      <td className="px-3 py-2">
                        <Input value={numbers[inv.id] ?? ""} placeholder="e.g. CE/26-27/SV101"
                          onChange={(e) => setNumbers((n) => ({ ...n, [inv.id]: e.target.value }))}
                          className={`h-8 w-48 mono text-xs ${missing ? "border-amber-400" : ""}`}
                          data-testid={`bulk-issue-tally-no-${inv.id}`} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {tab === "issued" && (
          <div className="space-y-2">
            <Input value={issuedSearch} onChange={(e) => setIssuedSearch(e.target.value)}
              placeholder="Search invoice no., DC or customer" className="h-9 max-w-sm" />
            <div className="overflow-x-auto border border-slate-200 rounded-md max-h-[50vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left">Invoice No.</th>
                    <th className="px-3 py-2 text-left">DC</th>
                    <th className="px-3 py-2 text-left">Customer</th>
                    <th className="px-3 py-2 text-left">Issued</th>
                    <th className="px-3 py-2 text-left">Tally Invoice No.</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {issued.length === 0 && (
                    <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">No issued invoices.</td></tr>
                  )}
                  {issued.map((inv) => {
                    const changed = numberFor(inv.id) && numberFor(inv.id) !== tallyNoOf(inv);
                    return (
                      <tr key={inv.id}>
                        <td className="px-3 py-2 mono text-xs">{inv.tally_number_pending ? "Awaiting Tally no." : inv.code}</td>
                        <td className="px-3 py-2 mono text-xs">{inv.dc_code || "—"}</td>
                        <td className="px-3 py-2">{inv.customer_name || "—"}</td>
                        <td className="px-3 py-2 mono text-xs">{fmtDate(inv.issued_at)}</td>
                        <td className="px-3 py-2">
                          <Input value={numbers[inv.id] ?? ""} placeholder="Tally voucher no."
                            onChange={(e) => setNumbers((n) => ({ ...n, [inv.id]: e.target.value }))}
                            className="h-8 w-48 mono text-xs" />
                        </td>
                        <td className="px-3 py-2">
                          <Button size="sm" variant="outline" disabled={busy || !changed} onClick={() => saveNumber(inv)}>
                            <Save className="w-3.5 h-3.5 mr-1" /> Save
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {confirmMissing && (
          <div className="border border-amber-300 bg-amber-50 rounded-md p-3 text-sm text-amber-900 space-y-2" data-testid="bulk-issue-missing-warning">
            <div className="flex items-center gap-1.5 font-semibold">
              <AlertTriangle className="w-4 h-4" /> {confirmMissing.length} selected invoice(s) have no Tally Invoice No.
            </div>
            <div className="text-xs mono max-h-32 overflow-y-auto">{confirmMissing.map((m) => m.dc_code || m.code).join(", ")}</div>
            <div className="text-xs">Invoices issued without a number keep "Awaiting Tally no." — enter it any time in the <b>Issued</b> tab.</div>
            <div className="flex gap-2">
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" disabled={busy || confirmMissing.length === selectedIds.length}
                onClick={() => runIssue(selectedIds.filter((id) => numberFor(id)))} data-testid="bulk-issue-skip-missing">
                Skip these & issue the rest ({selectedIds.length - confirmMissing.length})
              </Button>
              <Button size="sm" variant="outline" className="border-amber-400" disabled={busy}
                onClick={() => runIssue(selectedIds, true)} data-testid="bulk-issue-without-tally-no">
                Issue all {selectedIds.length} anyway — add Tally No. later
              </Button>
              <Button size="sm" variant="outline" onClick={() => setConfirmMissing(null)}>Go back</Button>
            </div>
          </div>
        )}

        <DialogFooter className="items-center">
          <span className="text-xs text-slate-500 mr-auto">{selectedIds.length} of {drafts.length} selected</span>
          <Button variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>Close</Button>
          {tab === "draft" && (
            <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={busy || !selectedIds.length}
              onClick={issueSelected} data-testid="bulk-issue-submit">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> {busy ? (progress ? `Issuing… ${progress.done}/${progress.total}` : "Working…") : `Issue Selected (${selectedIds.length})`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
